import * as Sentry from '@sentry/browser'
import { Descendant, Element } from 'slate'

import { SSMLTag } from '../components/ClipEditor/RichTextEditor/SSMLTag'
import { SSMLBlockTag, VoiceTagAttributes } from '../components/ClipEditor/RichTextEditor/types'
import { PodSettings } from '../components/NewHub/hooks/useClipData'
import type { Variable } from '../store/SSMLEditor/TemplatesSidebarStore'
import { StructuredApiError } from '../utils/billingErrors'
import { CSRF } from '../utils/CSRF'
import { ObjectUtils } from '../utils/ObjectUtils'
import type { SSMLSerializer } from '../utils/SSMLSerializer'
import { BaseRepository } from './BaseRepository'
import { ClipUploadObject, SynthesisInputElement, ValidateSSMLObject } from './clipsRepositoryQueries'

const constructSynthesisApiInputElement = async (
  serializer: SSMLSerializer,
  node: Descendant,
  variables: Record<string, Variable>,
  options?: { clipPrompt?: string },
): Promise<SynthesisInputElement | undefined> => {
  if (!Element.isElement(node) || node.type !== SSMLTag.VOICE) return

  if (!node.uuid || typeof node.uuid !== 'string') {
    Sentry.captureMessage('SSMLEditor: Invalid node uuid')
    return
  }

  const voiceNode = node as VoiceTagAttributes
  const promptToUse = voiceNode.prompt || options?.clipPrompt
  const ssml = await serializer.serializeSingleSlateNode(node, {
    variables: variables,
    clipPrompt: promptToUse,
    omitVoicePromptAttribute: true,
  })
  return {
    type: node.type as SSMLBlockTag,
    ssml,
    uuid: node.uuid,
    voice_name: node.type === SSMLTag.VOICE ? (node.name as string) : undefined,
    resembleFill: node.resembleFill as string,
  }
}

const constructWholeSynthesisAPIInput = async (
  serializer: SSMLSerializer,
  nodes: Descendant[],
  variables: Record<string, Variable>,
  options?: { clipPrompt?: string },
): Promise<SynthesisInputElement[]> => {
  const nodesPromises = nodes.map((x) => constructSynthesisApiInputElement(serializer, x, variables, options))
  const synInput = (await Promise.all(nodesPromises)).filter((x): x is SynthesisInputElement => x !== undefined)
  return synInput
}

const buildStreamError = async (response: Response, fallbackMessage: string) => {
  const json = await response.json().catch(() => null)
  const errorCode = json?.error_code || json?.details?.error_code || (response.status === 402 ? 'insufficient_balance' : undefined)
  const message = json?.message || json?.error || fallbackMessage

  if (errorCode) {
    return new StructuredApiError(message, response.status, errorCode, json?.details)
  }

  return new Error(message)
}

export class ClipsRepository extends BaseRepository {
  uploadClip(data: ClipUploadObject) {
    const formData = ObjectUtils.convertObjToFormData(data)
    return super.getHttpClientInstance().post<{ status?: string; recording_uuid?: string }>('/api/v1/voices/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  getAudioStream = async ({
    text,
    projectUuid,
    voiceUuid,
    precision,
    podUuid,
    useHd,
    applyCustomPronunciations,
    model,
    signal,
    segmentUuid,
    sampleRate,
  }: {
    text: string
    signal: AbortSignal
    projectUuid: string
    podUuid: string
    voiceUuid: string
    precision: string
    useHd?: boolean
    applyCustomPronunciations?: boolean
    model?: string
    segmentUuid?: string
    sampleRate?: number
  }) => {
    const requestBody = {
      data: text,
      precision,
      project_uuid: projectUuid,
      voice_uuid: voiceUuid,
      pod_uuid: podUuid,
      use_hd: useHd,
      apply_custom_pronunciations: applyCustomPronunciations,
      model,
      segment_uuid: segmentUuid,
      sample_rate: sampleRate,
    }

    const response = await fetch('/stream/segment', {
      body: JSON.stringify(requestBody),
      method: 'POST',
      signal,
      headers: {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw await buildStreamError(response, 'An error occurred while generating the audio')
    }

    return response.body
  }

  /**
   * Streams a complete clip with multiple segments, handling both cached and new synthesis.
   * This enables efficient playback when only some narrators need re-synthesis.
   *
   * @param podUuid - The clip/pod UUID
   * @param segments - Array of segment objects with uuid, ssml (if needs synthesis), voice_uuid, and use_cached flag
   * @param precision - Audio precision format (e.g., 'PCM_16')
   * @param useHd - Whether to use HD voice synthesis
   * @param model - The model to use for synthesis (e.g., 'multilingual', 'turbo')
   * @param signal - AbortSignal for cancellation
   */
  streamClip = async ({
    podUuid,
    segments,
    precision,
    useHd,
    applyCustomPronunciations,
    model,
    signal,
    sampleRate,
  }: {
    podUuid: string
    segments: Array<{
      uuid: string
      ssml: string | null
      voice_uuid: string
      use_cached: boolean
    }>
    precision?: string
    useHd?: boolean
    applyCustomPronunciations?: boolean
    model?: string
    signal?: AbortSignal
    sampleRate?: number
  }) => {
    const requestBody = {
      pod_uuid: podUuid,
      segments: segments,
      precision,
      use_hd: useHd,
      apply_custom_pronunciations: applyCustomPronunciations,
      model,
      sample_rate: sampleRate,
    }

    const response = await fetch('/stream/clip', {
      body: JSON.stringify(requestBody),
      method: 'POST',
      signal,
      headers: {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw await buildStreamError(response, 'An error occurred while streaming the clip')
    }

    return response.body
  }

  updateClip(
    projectId: string,
    podUUID: string,
    title: string,
    ssml: string,
    synthesisInput: Array<SynthesisInputElement>,
    synthesize: boolean,
    variables: Resemble.PodVariables,
    podSettings?: PodSettings,
  ) {
    return super.getHttpClientInstance().put(
      `/projects/${projectId}/clips/${podUUID}`,
      {
        title,
        segments: synthesisInput,
        ssml,
        synthesize,
        variables,
        pod_settings: podSettings,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }

  createSegment(projectId: string, clipUUID: string, segmentInput: SynthesisInputElement) {
    return super.getHttpClientInstance().post(`/projects/${projectId}/clips/${clipUUID}/segments`, segmentInput, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  attachDonorToSegment(projectId: string, clipUUID: string, segmentUuid: string, audio: Blob) {
    const data = {
      audio: audio,
    }

    return super.getHttpClientInstance().post(`/projects/${projectId}/clips/${clipUUID}/segments/${segmentUuid}/donor`, ObjectUtils.convertObjToFormData(data), {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  uploadSegmentAudio(projectId: string, clipUUID: string, segmentUuid: string, audio: Blob) {
    const data = {
      audio: audio,
    }

    return super.getHttpClientInstance().post(`/projects/${projectId}/clips/${clipUUID}/segments/${segmentUuid}/audio`, ObjectUtils.convertObjToFormData(data), {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  uploadAudioTagAudio(audio: Blob, name: string) {
    const data = {
      audio: audio,
      name: name,
    }

    return super.getHttpClientInstance().post('/api/v2/audios', ObjectUtils.convertObjToFormData(data), {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  uploadDemoAudioTagAudio(audio: Blob, voice: string) {
    const data = {
      audio: audio,
      voice: voice,
    }

    return super.getHttpClientInstance().post('/services/generate_stos', ObjectUtils.convertObjToFormData(data), {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  deleteAudioTagAudios(ids: number[]) {
    return super.getHttpClientInstance().delete(`/api/v2/audios/${ids.toString()}`, {
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  /**
   * Retrieves audio files that were uploaded as a donor for the audio/convert tag.
   */
  getAudioTagAudios() {
    return super.getHttpClientInstance().get('/api/v2/audios', {
      'X-CSRF-Token': CSRF.getToken(),
      Accept: 'application/json',
    })
  }

  createAndSynthesizeSegment(projectId: string, clipUUID: string, synthesisInput: Array<SynthesisInputElement>, ssml: string, variables: Record<string, string[]>) {
    return super.getHttpClientInstance().post(
      `/projects/${projectId}/clips/${clipUUID}/segments/async`,
      {
        segments: synthesisInput,
        ssml,
        variables,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }

  pollForSegments(projectId: string, clipUUID: string, segmentUUIDs: string[]) {
    return super.getHttpClientInstance().post(
      `/projects/${projectId}/clips/${clipUUID}/segments/polling_get_segment`,
      {
        segmentUUIDs: segmentUUIDs,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }

  pollForPod(projectId: string, clipUUID: string) {
    return super.getHttpClientInstance().get(`/projects/${projectId}/clips/${clipUUID}/polling_get_pod`, {
      'X-CSRF-Token': CSRF.getToken(),
      Accept: 'application/json',
    })
  }

  updateClipSettings(projectId: string, clipUUID: string, outputFormat?: string, precision?: string, sampleRate?: number) {
    return super.getHttpClientInstance().post(
      `/projects/${projectId}/clips/${clipUUID}/update_clip_settings`,
      {
        output_format: outputFormat,
        precision: precision,
        sample_rate: sampleRate,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }

  /**
   * Validates SSMl
   * @param ssml SSML object in the shape of ValidateSSMLObject
   */
  validateSSML(ssml: ValidateSSMLObject) {
    return super.getHttpClientInstance().post('/api/v1/clips/validate-ssml', ssml)
  }

  addFile(projectSlug: string, clipSlug: string, segmentId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    return super.getHttpClientInstance().post(`/projects/${projectSlug}/clips/${clipSlug}/segments/${segmentId}/addfile`, formData, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'multipart/form-data',
    })
  }

  moveClipToProject(clipUuid: string, projectUuid: string) {
    return super.getHttpClientInstance().patch(
      '/move_clip',
      {
        project_uuid: projectUuid,
        clip_uuid: clipUuid,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }

  scaffoldClipIntoProject = async (voiceUuid: string, projectUuid: string) => {
    return (
      await super.getHttpClientInstance().post<{ location: string }>(
        '/voices/create_demo_clip',
        {
          project_uuid: projectUuid,
          voice_uuid: voiceUuid,
        },
        {
          'X-CSRF-Token': CSRF.getToken(),
          Accept: 'application/json',
        },
      )
    ).data
  }

  regenerate(voiceUuid: string, data: string, options?: { signal?: AbortSignal }) {
    return super.getHttpClientInstance().post<{ audio_url: string; success: boolean }>(
      '/hub/regenerate',
      {
        voice_uuid: voiceUuid,
        data,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        signal: options?.signal,
      },
    )
  }

  public static helpers = {
    constructWholeSynthesisAPIInput,
  }

  setBestSegment(projectId: string, clipUUID: string, parentUUID: string, bestOfUUID: string, bestOfSsml: string, parentSsml: string) {
    return super.getHttpClientInstance().post(
      `/projects/${projectId}/clips/${clipUUID}/segments/set_best_segment`,
      {
        parent_uuid: parentUUID,
        best_of_uuid: bestOfUUID,
        best_of_ssml: bestOfSsml,
        parent_ssml: parentSsml,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }
}
